import { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, Send, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function LeadFormPage() {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ nombre: "", telefono: "", email: "", ramo: "", mensaje: "", website: "" });
  const [fields, setFields] = useState({});

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/pipelines/public/lead-form`);
        if (active) setSchema(res.data);
      } catch {
        if (active) setSchema({ available: true, pipeline_name: "", ramo: { options: [], required: true }, fields: [], show_email: true, show_mensaje: true });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateField = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const visibleFields = (schema?.fields || []).filter((f) => !f.ramos || f.ramos.length === 0 || f.ramos.includes(form.ramo));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.nombre.trim() || !form.telefono.trim()) { setError("Por favor completá tu nombre y teléfono."); return; }
    if (schema?.ramo?.required && !form.ramo) { setError("Seleccioná un ramo de interés."); return; }
    if (schema?.show_email && schema?.email_required && !form.email.trim()) { setError("El email es obligatorio."); return; }
    for (const f of visibleFields) {
      if (f.required && !String(fields[f.name] ?? "").trim()) { setError(`El campo "${f.label}" es obligatorio.`); return; }
    }
    setSubmitting(true);
    try {
      const payload = { ...form, fields: {} };
      visibleFields.forEach((f) => { if (fields[f.name] !== undefined) payload.fields[f.name] = fields[f.name]; });
      await axios.post(`${API}/pipelines/public/lead`, payload);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "No pudimos enviar tu solicitud. Intentá de nuevo en unos minutos.");
    } finally {
      setSubmitting(false);
    }
  };

  const ramos = schema?.ramo?.options || [];
  const b = schema?.branding || {};
  const primary = b.primary_color || "#193966";
  const accent = b.accent_color || "#80BC00";
  const inputCls = "w-full h-11 border border-slate-300 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:border-current";

  const renderDynamic = (f) => {
    const v = fields[f.name] ?? "";
    if (f.type === "select") {
      return (
        <select value={v} onChange={(e) => updateField(f.name, e.target.value)} className={inputCls + " bg-white"} data-testid={`lead-field-${f.name}`}>
          <option value="">Seleccioná…</option>
          {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (f.type === "textarea") {
      return <textarea value={v} onChange={(e) => updateField(f.name, e.target.value)} rows={3} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#193966]/30" data-testid={`lead-field-${f.name}`} />;
    }
    const htmlType = f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "phone" ? "tel" : f.type === "email" ? "email" : "text";
    return <input type={htmlType} value={v} onChange={(e) => updateField(f.name, e.target.value)} className={inputCls} data-testid={`lead-field-${f.name}`} />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4" data-testid="lead-form-page">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-6" style={{ backgroundColor: primary }}>
          {b.logo_url ? (
            <img src={b.logo_url} alt="Logo" className="h-12 mb-3 object-contain" data-testid="lead-logo" />
          ) : null}
          <div className="flex items-center gap-2 text-xs font-semibold tracking-wide uppercase mb-1" style={{ color: accent }}>
            <ShieldCheck className="w-4 h-4" /> {b.eyebrow || "QA Asesores · Corredores de Seguros"}
          </div>
          <h1 className="text-white text-2xl font-bold leading-tight">{b.title || "Solicitá tu cotización"}</h1>
          <p className="text-white/80 text-sm mt-1">{b.subtitle || "Dejanos tus datos y un asesor te contactará a la brevedad."}</p>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center py-12 text-slate-400"><Loader2 className="w-8 h-8 animate-spin mb-3" /> Cargando…</div>
          ) : done ? (
            <div className="flex flex-col items-center py-10 text-center" data-testid="lead-form-success">
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-4"><CheckCircle2 className="w-9 h-9 text-green-600" /></div>
              <p className="text-lg font-bold text-slate-800">{b.success_title || "¡Solicitud recibida!"}</p>
              <p className="text-sm text-slate-500 mt-2 max-w-sm">{b.success_message || "Gracias por contactarnos. Uno de nuestros asesores se comunicará contigo muy pronto."}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
              <input type="text" value={form.website} onChange={(e) => update("website", e.target.value)} tabIndex={-1} autoComplete="off" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} aria-hidden="true" />

              <Field label="Nombre completo *">
                <input type="text" value={form.nombre} onChange={(e) => update("nombre", e.target.value)} className={inputCls} placeholder="Tu nombre" data-testid="lead-nombre" required />
              </Field>
              <Field label="Teléfono *">
                <input type="tel" value={form.telefono} onChange={(e) => update("telefono", e.target.value)} className={inputCls} placeholder="809-000-0000" data-testid="lead-telefono" required />
              </Field>
              {schema?.show_email !== false && (
                <Field label={`Email${schema?.email_required ? " *" : ""}`}>
                  <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} placeholder="tu@email.com" data-testid="lead-email" />
                </Field>
              )}

              <Field label={`${schema?.ramo?.label || "Ramo de interés"}${schema?.ramo?.required ? " *" : ""}`}>
                <select value={form.ramo} onChange={(e) => update("ramo", e.target.value)} className={inputCls + " bg-white"} data-testid="lead-ramo">
                  <option value="">Seleccioná un ramo…</option>
                  {ramos.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>

              {/* Campos dinámicos / condicionales por ramo */}
              {visibleFields.map((f) => (
                <Field key={f.name} label={`${f.label}${f.required ? " *" : ""}`}>
                  {renderDynamic(f)}
                </Field>
              ))}

              {schema?.show_mensaje !== false && (
                <Field label="Mensaje">
                  <textarea value={form.mensaje} onChange={(e) => update("mensaje", e.target.value)} rows={3} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#193966]/30" placeholder="Contanos qué necesitás cotizar…" data-testid="lead-mensaje" />
                </Field>
              )}

              {error && <p className="text-sm text-red-600" data-testid="lead-error">{error}</p>}

              <button type="submit" disabled={submitting} className="w-full h-11 rounded-lg text-white font-semibold flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-60" style={{ backgroundColor: accent }} data-testid="lead-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitting ? "Enviando…" : "Enviar solicitud"}
              </button>
              <p className="text-[11px] text-slate-400 text-center">{b.footer_note || "Tus datos se usan únicamente para gestionar tu solicitud de cotización."}</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
    {children}
  </div>
);
